import { useState, useEffect } from "react";
import { DealbreakerQuestion, DealbreakerAnswer, DealbreakerAnswerInput } from "@/types/dealbreaker";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { CheckCircle2, Loader2 } from "lucide-react";

interface DealbreakerQuestionsCardProps {
  questions: DealbreakerQuestion[];
  existingAnswers: DealbreakerAnswer[];
  matchId: string;
  currentUserId: string;
  onSubmit: (answers: DealbreakerAnswerInput[]) => Promise<void>;
  submitting: boolean;
  disabled?: boolean;
}

export function DealbreakerQuestionsCard({
  questions,
  existingAnswers,
  matchId,
  currentUserId,
  onSubmit,
  submitting,
  disabled = false,
}: DealbreakerQuestionsCardProps) {
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [hasSubmitted, setHasSubmitted] = useState(false);

  // Initialize answers from existing answers or empty strings
  useEffect(() => {
    const initialAnswers: Record<string, string> = {};
    questions.forEach((q) => {
      const existingAnswer = existingAnswers.find(
        (a) => a.question_id === q.id && a.answered_by_profile_id === currentUserId
      );
      initialAnswers[q.id] = existingAnswer?.answer_text || "";
    });
    setAnswers(initialAnswers);

    // Check if user has already submitted answers
    setHasSubmitted(existingAnswers.some((a) => a.answered_by_profile_id === currentUserId));
  }, [questions, existingAnswers, currentUserId]);

  const updateAnswer = (questionId: string, text: string) => {
    setAnswers((prev) => ({ ...prev, [questionId]: text }));
  };

  const allQuestionsAnswered = questions.every(
    (q) => answers[q.id] && answers[q.id].trim().length > 0
  );

  const handleSubmit = async () => {
    if (!allQuestionsAnswered) return;

    const answersToSubmit: DealbreakerAnswerInput[] = questions.map((q) => ({
      question_id: q.id,
      answer_text: answers[q.id].trim(),
    }));

    await onSubmit(answersToSubmit);
    setHasSubmitted(true);
  };

  if (questions.length === 0) {
    return null;
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Dealbreaker Questions</CardTitle>
        <CardDescription>
          This person has created some important questions they'd like you to answer.
          {!hasSubmitted && " Please answer all questions to proceed."}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {hasSubmitted && (
          <Alert className="bg-green-50 border-green-200">
            <CheckCircle2 className="h-4 w-4 text-green-600" />
            <AlertDescription className="text-green-800">
              Answers submitted successfully! You can update your answers below and resubmit if needed.
            </AlertDescription>
          </Alert>
        )}

        {questions.map((question, index) => (
          <div key={question.id} className="space-y-2">
            <Label htmlFor={`answer-${question.id}`} className="text-base font-semibold">
              Question {index + 1}
            </Label>
            <p className="text-sm text-muted-foreground mb-2">{question.question_text}</p>
            <Textarea
              id={`answer-${question.id}`}
              placeholder="Type your answer here..."
              value={answers[question.id] || ""}
              onChange={(e) => updateAnswer(question.id, e.target.value)}
              maxLength={500}
              rows={4}
              className="resize-none"
              disabled={disabled}
            />
            <div className="text-xs text-muted-foreground text-right">
              {(answers[question.id] || "").length}/500 characters
            </div>
          </div>
        ))}

        <div className="space-y-4">
          <Button
            onClick={handleSubmit}
            disabled={!allQuestionsAnswered || submitting || disabled}
            className="w-full"
            size="lg"
          >
            {submitting ? (
              <>
                <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                {hasSubmitted ? "Updating Answers..." : "Submitting Answers..."}
              </>
            ) : (
              <>{hasSubmitted ? "Update Answers" : "Submit Answers"}</>
            )}
          </Button>

          {!allQuestionsAnswered && (
            <p className="text-sm text-muted-foreground text-center">
              Please answer all questions to enable submission
            </p>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
